import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Search,
    Download,
    Mail,
    Phone,
    User,
    Calendar,
    Tag,
    Ticket,
    RefreshCw,
    Users
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Lead {
    id: number;
    created_at: string;
    name: string | null;
    email: string | null;
    telefone: string | null; // ou phone
    phone?: string | null;   // fallback
    promocaoLead: string | null;
    campaign_title?: string | null;
    CUPOM: string | null;
    user_id?: string;
}

export default function LeadsSection() {
    const { user } = useAuth();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Buscar leads do usuário
    useEffect(() => {
        const fetchLeads = async () => {
            if (!user?.id) return;

            setLoading(true);
            try {
                console.log('🔍 Buscando leads para user_id:', user.id);

                // Buscar leads onde user_id corresponde ao usuário logado
                const { data, error } = await supabase
                    .from('leads')
                    .select('*')
                    .eq('user_id', user.id)
                    .order('created_at', { ascending: false });

                if (error) {
                    console.error('❌ Erro ao buscar leads:', error);
                    // Tentar query alternativa caso user_id não esteja populado em leads antigos
                    // Buscar promoções do usuário para filtrar por nome
                    const { data: promos } = await supabase
                        .from('promos')
                        .select('title')
                        .eq('user_id', user.id);

                    if (promos && promos.length > 0) {
                        const promoTitles = promos.map(p => p.title);
                        const { data: leadsByTitle } = await supabase
                            .from('leads')
                            .select('*')
                            .in('promocaoLead', promoTitles)
                            .order('created_at', { ascending: false });

                        if (leadsByTitle) {
                            setLeads(leadsByTitle as any[]);
                        }
                    }
                } else {
                    setLeads(data as any[] || []);
                }
            } catch (err) {
                console.error('Erro inesperado ao buscar leads:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchLeads();
    }, [user?.id, refreshTrigger]);

    // Filtragem local
    const filteredLeads = useMemo(() => {
        if (!searchTerm.trim()) return leads;

        const term = searchTerm.toLowerCase();
        return leads.filter(lead =>
            (lead.name?.toLowerCase().includes(term)) ||
            (lead.email?.toLowerCase().includes(term)) ||
            (lead.telefone?.includes(term)) ||
            (lead.phone?.includes(term)) ||
            (lead.promocaoLead?.toLowerCase().includes(term)) ||
            (lead.CUPOM?.toLowerCase().includes(term))
        );
    }, [leads, searchTerm]);

    // Estatísticas rápidas
    const stats = useMemo(() => {
        return {
            total: leads.length,
            thisMonth: leads.filter(l => {
                const date = new Date(l.created_at);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            }).length,
            withPhone: leads.filter(l => l.telefone || l.phone).length,
            withEmail: leads.filter(l => l.email).length
        };
    }, [leads]);

    const handleExport = () => {
        if (leads.length === 0) return;

        // Criar CSV
        const headers = ['Data', 'Nome', 'Email', 'Telefone', 'Campanha/Promoção', 'Cupom'];
        const csvContent = [
            headers.join(','),
            ...leads.map(lead => [
                new Date(lead.created_at).toLocaleString('pt-BR'),
                `"${lead.name || ''}"`,
                lead.email || '',
                lead.telefone || lead.phone || '',
                `"${lead.promocaoLead || lead.campaign_title || ''}"`,
                lead.CUPOM || ''
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `leads_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="flex gap-4">
                    <Skeleton className="h-10 flex-1" />
                    <Skeleton className="h-10 w-32" />
                </div>
                <div className="border rounded-md">
                    <div className="p-4 space-y-4">
                        {[1, 2, 3].map(i => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (leads.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50/50 dark:bg-gray-900/50 text-center">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-full mb-4 shadow-sm">
                    <Users className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Nenhum lead capturado ainda
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mb-6">
                    Seus leads aparecerão aqui quando os espectadores interagirem com suas campanhas e preencherem os formulários de contato.
                </p>
                <Button variant="outline" onClick={() => setRefreshTrigger(p => p + 1)}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header com Stats e Busca */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-end">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto flex-1">
                    <Card className="p-4 border-l-4 border-l-blue-500 shadow-sm">
                        <p className="text-xs text-muted-foreground uppercase font-bold">Total de Leads</p>
                        <p className="text-2xl font-bold">{stats.total}</p>
                    </Card>
                    <Card className="p-4 border-l-4 border-l-green-500 shadow-sm">
                        <p className="text-xs text-muted-foreground uppercase font-bold">Este Mês</p>
                        <p className="text-2xl font-bold">{stats.thisMonth}</p>
                    </Card>
                    <Card className="p-4 border-l-4 border-l-amber-500 shadow-sm">
                        <p className="text-xs text-muted-foreground uppercase font-bold">Com WhatsApp</p>
                        <p className="text-2xl font-bold">{stats.withPhone}</p>
                    </Card>
                    <Card className="p-4 border-l-4 border-l-purple-500 shadow-sm">
                        <p className="text-xs text-muted-foreground uppercase font-bold">Com Email</p>
                        <p className="text-2xl font-bold">{stats.withEmail}</p>
                    </Card>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-card p-4 rounded-lg border shadow-sm">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por nome, email, cupom..."
                        className="pl-9"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <Button variant="outline" onClick={() => setRefreshTrigger(p => p + 1)}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Atualizar
                    </Button>
                    <Button variant="default" className="bg-green-600 hover:bg-green-700" onClick={handleExport}>
                        <Download className="mr-2 h-4 w-4" />
                        Exportar CSV
                    </Button>
                </div>
            </div>

            {/* Tabela de Leads */}
            <div className="rounded-md border bg-card shadow-sm overflow-hidden">
                <Table>
                    <TableHeader className="bg-muted/50">
                        <TableRow>
                            <TableHead className="w-[100px]">Data</TableHead>
                            <TableHead>Espectador</TableHead>
                            <TableHead>Contatos</TableHead>
                            <TableHead>Campanha / Origem</TableHead>
                            <TableHead>Cupom</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredLeads.map((lead) => (
                            <TableRow key={lead.id} className="hover:bg-muted/50 transition-colors">
                                <TableCell className="font-medium text-xs text-muted-foreground">
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-foreground">
                                            {format(new Date(lead.created_at), 'dd/MM/yyyy')}
                                        </span>
                                        <span>
                                            {format(new Date(lead.created_at), 'HH:mm')}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                            <User className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{lead.name || 'Anônimo'}</p>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="space-y-1">
                                        {lead.email && (
                                            <div className="flex items-center gap-2 text-xs">
                                                <Mail className="h-3 w-3 text-muted-foreground" />
                                                <span className="truncate max-w-[180px]" title={lead.email}>
                                                    {lead.email}
                                                </span>
                                            </div>
                                        )}
                                        {(lead.telefone || lead.phone) && (
                                            <div className="flex items-center gap-2 text-xs">
                                                <Phone className="h-3 w-3 text-muted-foreground" />
                                                <span>{lead.telefone || lead.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge variant="secondary" className="font-normal">
                                        <Tag className="mr-1 h-3 w-3" />
                                        {lead.promocaoLead || lead.campaign_title || 'Geral'}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {lead.CUPOM ? (
                                        <Badge variant="outline" className="font-mono text-xs border-green-500 text-green-600 bg-green-50 dark:bg-green-900/10">
                                            <Ticket className="mr-1 h-3 w-3" />
                                            {lead.CUPOM}
                                        </Badge>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className="text-center text-xs text-muted-foreground py-2">
                Mostrando {filteredLeads.length} leads
            </div>
        </div>
    );
}
